import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import {
  Button,
  Dropdown,
  Form,
  FormField,
  FormGroup,
  Icon,
  Label,
  TextArea,
} from "semantic-ui-react";

// Common
import DateTextBox from "react-lib/apps/common/DateTextBox";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import Dropzone, { DropFilesEventHandler } from "react-dropzone";

// Utils
import moment, { Moment } from "moment";
import { beToAd, formatDateDashToSlash } from "react-lib/utils/dateUtils";

// Interface
import {
  CARD_NAME,
  SetProp,
  RunSequence,
  PickedProps,
  leaveOptions,
  LeaveRemainingType,
} from "./sequence/SelfServiceSchedule";

// Serializer
import LeaveTypeSerializer from "issara-sdk/types/LeaveTypeSerializer_apps_HRM";
import LeaveRequestSerializer from "issara-sdk/types/LeaveRequestSerializer_apps_HRM";

type CardLeaveRequestPanelProps = {
  mode: "create" | "edit";
  initValue?: Partial<LeaveRequestSerializer>;
  existFiles?: { file: File; id?: number }[];
  onSubmit: (
    formState: Partial<LeaveRequestSerializer>,
    files: ({ file: File } | { id: number })[],
    id?: number
  ) => void;
  onCancel: () => void;
  // seq
  setProp: SetProp;
  runSequence: RunSequence;
  // data
  leaveTypeList: LeaveTypeSerializer[];
  leaveRemaining: LeaveRemainingType;
  paramKey: string;
} & PickedProps;

type Styles = {
  [key in "dropzone"]: CSSProperties;
};

type UploadedFileType = { file: File; url?: string; id?: number };

const styles: Styles = {
  dropzone: {
    display: "flex",
    alignContent: "center",
    alignItems: "center",
    flexDirection: "column",
    border: "2px dashed #b2b2c8",
    borderRadius: "5px",
    cursor: "pointer",
    padding: "3rem",
  },
};

const CardLeaveRequestPanel = (props: CardLeaveRequestPanelProps) => {
  const [selectedLeave, setSelectedLeave] = useState<Partial<LeaveTypeSerializer> | null>(null);
  const [formState, setFormState] = useState<Partial<LeaveRequestSerializer>>({});
  const [files, setFiles] = useState<UploadedFileType[]>([]);

  const existFilesRef = useRef<UploadedFileType[]>([]);

  useEffect(() => {
    if (props.initValue) {
      const formattedValue: Partial<LeaveRequestSerializer> = {
        ...props.initValue,
        start_date: formatDateDashToSlash(props.initValue?.start_date || null),
        end_date: formatDateDashToSlash(props.initValue?.end_date || null),
        id: props.initValue.id,
      };

      setFormState(formattedValue);
    }
  }, [props.initValue]);

  useEffect(() => {
    if (props.existFiles) {
      const decodedFiles = props.existFiles.map((fileObj: any) => {
        const url = typeof fileObj === "string" ? fileObj : fileObj.file;
        const fileName = extractFileName(url);
        const mockFile = new File([""], fileName); //! issue 77387

        return { file: mockFile, url, id: fileObj.id };
      });
      setFiles(decodedFiles);
      existFilesRef.current = decodedFiles;
    }
  }, [props.existFiles]);

  useEffect(() => {
    if (formState.leave_type) {
      const matchedLeave = props.leaveTypeList.find((lt) => lt.id === formState.leave_type);
      setSelectedLeave(matchedLeave || null);
    }
  }, [formState.leave_type, props.leaveTypeList]);

  useEffect(() => {
    if (selectedLeave?.id) {
      props.runSequence({
        sequence: "SelfServiceSchedule",
        menu: "Common",
        action: "REMAIN",
        leaveType: selectedLeave.id,
      });
    }
  }, [selectedLeave?.id]);

  const isEditMode = props.mode === "edit";
  const isHalfDay = useMemo(() => formState?.day_type === "H", [formState]);

  const leaveTypeOptions = useMemo(() => {
    return (props.leaveTypeList || []).map((item: Partial<LeaveTypeSerializer>) => ({
      key: item.id,
      value: item.id,
      text: item.name,
    }));
  }, [props.leaveTypeList]);

  const leaveOptionsFiltered = useMemo(() => {
    if (selectedLeave?.allow_half_day_leave) {
      return leaveOptions;
    }

    return leaveOptions.filter((option) => option.value === "F");
  }, [selectedLeave]);

  const leaveRemaining = useMemo(() => {
    return props.leaveRemaining?.approve_remaining_leave_days || 0;
  }, [props.leaveRemaining?.approve_remaining_leave_days]);

  const handleChangeData = (e: any, data: any) => {
    const { name, value, checked, type } = data;

    const newValue = typeof checked === "boolean" ? checked : value;

    setFormState((prev) => {
      const updated: Partial<LeaveRequestSerializer> = {
        ...prev,
        [name]: newValue,
      };

      return updated;
    });
  };

  const handleChangeDate = (name: "start_date" | "end_date") => (value: string) => {
    handleChangeData(null, { name, value });

    if (name == "start_date" && formState?.day_type === "H") {
      handleChangeData(null, { name: "end_date", value });
    }
  };

  const handleChangeLeaveType = (e: React.SyntheticEvent<HTMLElement>, data: any) => {
    const selected = (props.leaveTypeList || []).find((item) => item.id === data.value);

    setSelectedLeave(selected || null);

    handleChangeData(null, {
      name: data.name,
      value: selected?.id,
    });
  };

  const handleChangeFile: DropFilesEventHandler = useCallback((acceptedFiles, rejectedFiles) => {
    const validFiles = acceptedFiles.filter((file) => file instanceof File);

    if (rejectedFiles.length > 0) {
      console.warn(`มีประเภทไฟล์ไม่ถูกต้อง: ${rejectedFiles}`);
    }

    const formatted = validFiles.map((file) => ({ file }));

    setFiles((prev) => [...prev, ...formatted]);
  }, []);

  const handleClearFile = useCallback((indexToRemove: number) => {
    setFiles((prevFile) => prevFile.filter((_, index) => index !== indexToRemove));
  }, []);

  const handleRequest = () => {
    // const newFilesOnly = files.filter((f) => !f.url);
    const filePayload = files.map((f) => {
      if (f.id) {
        return { id: f.id };
      }

      return { file: f.file };
    })
    props.onSubmit?.(formState, filePayload, formState.id);
  };

  const handleCancel = () => {
    props.onCancel?.();
  };

  return (
    <Form style={{ marginLeft: "1rem", maxHeight: "60vh", overflowX: "hidden", overflowY: "auto" }}>
      {/* Select Type Section */}
      <FormGroup inline>
        <FormField inline width={2}>
          <label id="CardLeaveRequestPanel-Label-Title">Leave Type</label>
        </FormField>
        <FormField inline>
          <div style={{ width: "100%" }}>
            <Dropdown
              id="CardLeaveRequest-Dropdown-LeaveType"
              disabled={isEditMode}
              selection
              name="leave_type"
              value={formState?.leave_type || ""}
              options={leaveTypeOptions || []}
              onChange={handleChangeLeaveType}
            />
          </div>
        </FormField>
        <FormField inline>
          <label>Remaining: </label>
          <span>{leaveRemaining ? leaveRemaining : ""}</span>
        </FormField>
      </FormGroup>
      {/* Leave Options Section */}
      <FormGroup inline>
        <FormField inline width={2}>
          <label>Leave Options</label>
        </FormField>
        <FormField inline>
          <div style={{ width: "100%" }}>
            <Dropdown
              id="CardLeaveRequest-Dropdown-DayType"
              disabled={isEditMode}
              selection
              options={leaveOptionsFiltered || []}
              onChange={handleChangeData}
              value={formState?.day_type || ""}
              name="day_type"
            />
          </div>
        </FormField>
      </FormGroup>
      {/* Date Section */}
      <FormGroup inline>
        <FormField inline width={2}>
          <label>Date</label>
        </FormField>
        <FormField inline width={3}>
          <div style={{ width: "100%" }}>
            <DateTextBox
              id="CardLeaveRequest-DateTextBox-StartDate"
              disabled={isEditMode}
              // useAdDate={true}
              value={formState?.start_date || ""}
              onChange={handleChangeDate("start_date")}
            />
          </div>
        </FormField>
        {!isHalfDay && (
          <FormField inline width={3}>
            <div style={{ width: "100%" }}>
              <DateTextBox
                id="CardLeaveRequest-DateTextBox-EndDate"
                disabled={isEditMode}
                // useAdDate={true}
                value={formState?.end_date || ""}
                onChange={handleChangeDate("end_date")}
              />
            </div>
          </FormField>
        )}
        <FormField inline>
          <label>Total: </label>
          <span>
            {formState?.start_date && formState?.end_date
              ? `${calculateDays(formState?.start_date, formState?.end_date, formState?.day_type)} day(s)`
              : ""}
          </span>
        </FormField>
      </FormGroup>
      {/* Description Section */}
      <FormGroup>
        <FormField width={8}>
          <label>Description</label>
          <TextArea
            id="CardLeaveRequest-TextArea-Description"
            name="description"
            onChange={handleChangeData}
            value={formState?.description || ""}
          />
        </FormField>
      </FormGroup>
      {/* Attach File Section */}
      {selectedLeave?.attachment_required ? (
        <FormGroup>
          <FormField width={8}>
            <label>Attach File</label>
            <Dropzone
              id="CardLeaveRequest-Dropzone-AttachFile"
              multiple
              onDrop={handleChangeFile}
              accept={".pdf, .jpg, .jpeg, .png"}
              style={styles.dropzone}
            >
              <Label size="big" style={{ backgroundColor: "white" }} circular>
                <Icon name="cloud upload" size="big" style={{ color: "2563eb", margin: 0 }} />
              </Label>
              <label style={{ color: "gray" }}>Choose file or drage it here</label>
              {files.length > 0 && (
                <ul>
                  {files.map(({ file }, index) => (
                    <div
                      style={{
                        alignItems: "baseline",
                        background: "white",
                        borderRadius: "8px",
                        color: "#4b5563",
                        display: "flex",
                        marginTop: "1rem",
                        padding: "0.5rem 1rem",
                      }}
                    >
                      <Icon name="file alternate outline" />
                      {file.name}
                      <Icon
                        color="blue"
                        name="close"
                        style={{ marginLeft: "10rem" }}
                        onClick={(e: any) => {
                          e?.stopPropagation();
                          handleClearFile(index);
                        }}
                      />
                    </div>
                  ))}
                </ul>
              )}
            </Dropzone>
          </FormField>
        </FormGroup>
      ) : null}
      {/* Button Section */}
      <FormGroup inline>
        <FormField inline width={9}>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", width: "100%" }}>
            <ButtonLoadCheck
              id="CardLeaveRequest-ButtonLoadCheck-Request"
              // func
              setProp={props.setProp}
              onClick={handleRequest}
              // data
              paramKey={isEditMode ? `${props.paramKey}_EDIT` : `${props.paramKey}_REQUEST`}
              buttonLoadCheck={
                isEditMode
                  ? props.buttonLoadCheck?.[`${props.paramKey}_EDIT`]
                  : props.buttonLoadCheck?.[`${props.paramKey}_REQUEST`]
              }
              // config
              color={isEditMode ? "orange" : "green"}
              title={isEditMode ? "EDIT" : "REQUEST"}
              name={isEditMode ? "EDIT" : "REQUEST"}
              size="medium"
            />
            <Button
              id="CardLeaveRequest-Button-Close"
              color="red"
              onClick={handleCancel}
            >
              CLOSE
            </Button>
          </div>
        </FormField>
      </FormGroup>
    </Form>
  );
};

/**========================================================================
 **                              UTILS
 *========================================================================**/

const calculateDays = (
  startDate: Moment | string | undefined,
  endDate: Moment | string | undefined,
  dayType?: "F" | "H",
  incldeEndDay: boolean = true
): number => {
  if (!startDate || !endDate) return 0;

  if (dayType === "H") return 0.5;

  const start = moment.isMoment(startDate)
    ? startDate.clone().startOf("day")
    : beToAd(startDate)?.startOf("day");
  const end = moment.isMoment(endDate)
    ? endDate.clone().startOf("day")
    : beToAd(endDate)?.startOf("day");

  if (!start?.isValid() || !end?.isValid()) return 0;

  const diff = end.diff(start, "days");

  return diff >= 0 ? diff + (incldeEndDay ? 1 : 0) : 0;
};

const extractFileName = (url: string) => {
  try {
    const pathname = new URL(url).pathname;
    const segments = pathname.split("/");
    const lastSegment = segments[segments.length - 1] || "unnamed_file";

    return decodeURIComponent(lastSegment);
  } catch {
    return "invalid_file";
  }
};

export default CardLeaveRequestPanel;
